import { Dispatch, SetStateAction, useCallback, useState } from 'react';
import * as R from 'remeda';
import { useIntl } from 'react-intl';
import { SelectionHandlers } from '@rossum/components/selection-handlers/SelectionHandlers';
import { Button, Stack } from '@rossum/ui/material';
import { GridFilterInputValueProps } from '@rossum/ui/x-data-grid-pro';
import { getStringArrayValueWithDefault } from '../../../mql/helpers';
import { QueueSelectList } from '../../../queues/select-queue/QueueSelectList';
import { useWorkspacesWithQueues } from './useWorkspacesWithQueues';

type CustomSelectProps = {
  gridFilterInputValueProps: GridFilterInputValueProps;
  handleClose: Dispatch<SetStateAction<boolean>>;
};

const convertToNumber = (value: string[]): number[] =>
  value.flatMap(val => Number(val) || []);

export const FieldsManagerQueueSelect = ({
  gridFilterInputValueProps,
  handleClose,
}: CustomSelectProps) => {
  const intl = useIntl();

  const topSelections = getStringArrayValueWithDefault(
    gridFilterInputValueProps?.item.value
  );

  const [selectedQueues, setSelectedQueues] = useState<string[]>(topSelections);

  const { workspacesWithQueues: queuesByWorkspace, isLoading } =
    useWorkspacesWithQueues();

  const queuesIds = queuesByWorkspace?.flatMap(w =>
    w.queues.map(queue => `${queue.id}`)
  );

  const hasFixedWidth = true;
  const SIMPLE_COMPONENT_WIDTH = 352;

  const handleSubmit = useCallback(() => {
    const newValue = R.unique(selectedQueues);
    gridFilterInputValueProps.applyValue({
      ...gridFilterInputValueProps.item,
      value: newValue,
    });
  }, [gridFilterInputValueProps, selectedQueues]);

  return (
    <Stack
      p={2}
      alignItems="flex-start"
      spacing={2}
      component="form"
      onSubmit={e => {
        e.preventDefault();
        e.stopPropagation();
        handleSubmit();
        handleClose(false);
      }}
      sx={{ width: hasFixedWidth ? SIMPLE_COMPONENT_WIDTH : 'auto' }}
    >
      <Stack
        sx={{
          width: '100%',
          flexDirection: 'row',
          justifyContent: 'space-between',
        }}
      >
        {queuesIds && (
          <SelectionHandlers
            selectedValues={selectedQueues}
            valueOptions={queuesIds}
            setValues={setSelectedQueues}
          />
        )}
      </Stack>
      <Stack sx={{ width: 1 }}>
        <QueueSelectList
          topSelections={convertToNumber(topSelections)}
          onSelect={ids => setSelectedQueues(ids.map(id => id.toString()))}
          selectedQueues={convertToNumber(selectedQueues)}
          queuesByWorkspace={queuesByWorkspace ?? []}
          dense
          isLoading={isLoading}
          searchInputProps={{ dataCy: 'queue-filter-component-search-input' }}
        />
      </Stack>

      <Button color="primary" variant="contained" type="submit">
        {intl.formatMessage({
          id: 'features.fieldManager.overview.queueSelectList.button.apply',
        })}
      </Button>
    </Stack>
  );
};
